import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useParams } from "react-router-dom";
import { Menu, Zap } from "lucide-react";
import {
  useAuthStore,
  useIsPendingDriver,
  useSoloDriverMembership,
  useBusinessMemberships,
} from "@/stores/authStore";
import { Logo } from "@/components/ui/Logo";
import { supabase } from "@/lib/supabase";
import { cn } from "@ridereserv/shared/utils";
import { clearUpgradeDismissals, useUpgradeDismissal } from "@/hooks/useUpgradeDismissal";
import { Badge } from "@/components/ui/Badge";
import { UpgradePlanCard } from "@/components/upgrade/UpgradePlanCard";
import { useSidebarNavigation } from "@/hooks/useSidebarNavigation";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { ThemeToggle } from "@/components/ui/ThemeToggle";

/**
 * Ink-rail nav row recipe (shared shell chrome — keep in sync with AccountLayout).
 * @param isActive - Whether the row's route is active.
 * @returns The className string for the nav row.
 */
const navRowClass = (isActive: boolean) =>
  cn(
    "relative flex min-h-11 items-center gap-2.5 rounded-sm px-2 text-sm font-medium transition-colors pointer-fine:min-h-8",
    isActive
      ? "bg-sidebar-active text-sidebar-foreground"
      : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
  );

/**
 * Cobalt active-route indicator rendered inside active nav rows.
 * @returns The indicator element.
 */
const ActiveIndicator = () => (
  <span
    aria-hidden
    className="absolute inset-y-1.5 -left-2 w-0.5 rounded-full bg-sidebar-accent"
  />
);

/**
 * Layout wrapper for authenticated dashboard pages.
 * Uses semantic color tokens and Lucide icons from the design system.
 * @returns The dashboard layout component with sidebar navigation
 */
export function DashboardLayout() {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { user, memberships, clearAuth, setSelectedBusinessId } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isPendingDriver = useIsPendingDriver();
  const soloDriverMembership = useSoloDriverMembership();
  const businessMemberships = useBusinessMemberships();

  // Sync URL companyId to store on mount and when it changes
  useEffect(() => {
    if (companyId) {
      setSelectedBusinessId(companyId);
    }
  }, [companyId, setSelectedBusinessId]);

  const currentCompany = memberships.find((m) => m.companyId === companyId);
  const currentRoles = currentCompany?.roles ?? [];

  // Check if user is a driver (has 'driver' role in any company)
  const isDriver = memberships.some((m) => m.roles.includes("driver"));

  // Check if user is corporate staff (has admin or manager role in any company)
  const isCorporateStaff = memberships.some((m) =>
    m.roles.some((role) => ["admin", "manager"].includes(role))
  );

  // Show upgrade CTAs only to free solo drivers who don't have admin/manager at a business company
  const isFreeDriver = soloDriverMembership?.subscriptionTierId === "free_driver";
  const hasBusinessAdminRole = businessMemberships.length > 0;
  const showUpgradeCta = isFreeDriver && !hasBusinessAdminRole;

  // Upgrade dismissal state
  const { isDismissed: isUpgradeDismissed, dismiss: dismissUpgrade } =
    useUpgradeDismissal("sidebar");

  const hasRole = (roles?: string[]) => {
    if (!roles) return true;
    return roles.some((role) => currentRoles.includes(role as never));
  };

  // Use shared sidebar navigation configuration for consistency across layouts
  const { personalNavItems, driverNavItems, getBusinessNavItems } = useSidebarNavigation();
  const companyNavItems = companyId ? getBusinessNavItems(companyId) : [];

  const handleLogout = () => {
    void supabase.auth.signOut().then(() => {
      clearAuth();
      clearUpgradeDismissals();
      void navigate("/login");
    });
  };

  const handleBusinessSwitch = (newBusinessId: string) => {
    setSelectedBusinessId(newBusinessId);
    void navigate(`/${newBusinessId}/dashboard`);
    setSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900/60 lg:hidden"
          onClick={() => {
            setSidebarOpen(false);
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-56 transform border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:shadow-none",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Brand block */}
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-sidebar-border px-4">
            <Logo asLink size="sm" tone="inverse" />
            <div className="hidden lg:flex">
              <NotificationBell tone="inverse" />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-2 py-3">
            {/* Personal section */}
            <p className="mb-1 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
              Personal
            </p>
            <ul className="space-y-0.5">
              {personalNavItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    end={item.href === "/account"}
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                    className={({ isActive }) => navRowClass(isActive)}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <ActiveIndicator />}
                        <item.icon
                          className={cn("h-4 w-4 shrink-0", isActive && "text-sidebar-accent")}
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                        <span className="flex-1">{item.label}</span>
                        {item.badge !== undefined && item.badge > 0 && (
                          <span
                            data-testid="notification-badge"
                            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive-500 px-1.5 text-xs font-medium text-white"
                          >
                            {item.badge > 99 ? "99+" : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Driver section (shown if user is a driver OR has pending application) */}
            {(isDriver || isPendingDriver) && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Driver
                </p>
                {/* Pending approval notification */}
                {isPendingDriver && (
                  <div className="mb-2 rounded-sm bg-warning-500/10 px-2 py-1.5">
                    <p className="text-xs font-medium text-warning-500">
                      Approval Pending
                    </p>
                    <p className="mt-0.5 text-xs text-sidebar-muted">
                      Your application is under review.
                    </p>
                  </div>
                )}
                <ul className="space-y-0.5">
                  {driverNavItems.map((item) => (
                    <li key={item.href}>
                      {isPendingDriver ? (
                        <span className="flex min-h-11 cursor-not-allowed items-center gap-2.5 rounded-sm px-2 text-sm font-medium text-sidebar-muted/50 pointer-fine:min-h-8">
                          <item.icon
                            className="h-4 w-4 shrink-0"
                            strokeWidth={1.75}
                            aria-hidden="true"
                          />
                          <span>{item.label}</span>
                        </span>
                      ) : (
                        <NavLink
                          to={item.href}
                          onClick={() => {
                            setSidebarOpen(false);
                          }}
                          className={({ isActive }) => navRowClass(isActive)}
                        >
                          {({ isActive }) => (
                            <>
                              {isActive && <ActiveIndicator />}
                              <item.icon
                                className={cn(
                                  "h-4 w-4 shrink-0",
                                  isActive && "text-sidebar-accent"
                                )}
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                              <span>{item.label}</span>
                            </>
                          )}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Become a Driver CTA (only shown if user is NOT a driver, NOT pending, and NOT corporate staff) */}
            {!isDriver && !isPendingDriver && !isCorporateStaff && (
              <div className="mt-6 rounded-md border border-sidebar-border bg-sidebar-hover p-3">
                <p className="text-sm font-medium text-sidebar-foreground">
                  Want to earn money?
                </p>
                <p className="mt-1 text-xs text-sidebar-muted">
                  Register as a driver and start accepting rides.
                </p>
                <NavLink
                  to="/account/become-driver"
                  className="mt-3 flex min-h-11 items-center justify-center rounded-sm bg-primary-600 px-4 text-sm font-medium text-white transition-colors hover:bg-primary-500 pointer-fine:min-h-8"
                >
                  Become a Driver
                </NavLink>
              </div>
            )}

            {/* Business Management section */}
            {businessMemberships.length > 0 && currentCompany && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Business
                </p>
                {/* Business selector */}
                <div className="mb-2">
                  <select
                    value={companyId ?? ""}
                    onChange={(e) => { handleBusinessSwitch(e.target.value); }}
                    aria-label="Switch business"
                    className="block min-h-11 w-full rounded-sm border border-sidebar-border bg-sidebar-hover px-2 text-sm text-sidebar-foreground [color-scheme:dark] focus:border-sidebar-accent focus:outline-none focus:ring-1 focus:ring-sidebar-accent pointer-fine:min-h-8"
                  >
                    {businessMemberships.map((membership) => (
                      <option key={membership.companyId} value={membership.companyId}>
                        {membership.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Business nav items */}
                <ul className="space-y-0.5">
                  {companyNavItems.filter((item) => hasRole(item.roles)).map((item) => (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        end={item.href === `/${companyId ?? ""}/dashboard`}
                        onClick={() => {
                          setSidebarOpen(false);
                        }}
                        className={({ isActive }) => navRowClass(isActive)}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <ActiveIndicator />}
                            <item.icon
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isActive && "text-sidebar-accent"
                              )}
                              strokeWidth={1.75}
                              aria-hidden="true"
                            />
                            <span>{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Upgrade section - only for free solo drivers without business admin/manager roles */}
            {showUpgradeCta && !isUpgradeDismissed && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Upgrade
                </p>

                {/* Upgrade Plan nav item */}
                <div className="group relative">
                  <NavLink
                    to="/account/select-plan"
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                    className={({ isActive }) => navRowClass(isActive)}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <ActiveIndicator />}
                        <Zap
                          className="h-4 w-4 shrink-0 text-upgrade-500"
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                        <span className="flex-1">Upgrade Plan</span>
                        <Badge
                          variant="default"
                          className="bg-upgrade-600 text-xs hover:bg-upgrade-600"
                        >
                          Pro
                        </Badge>
                      </>
                    )}
                  </NavLink>
                </div>

                {/* Upgrade card */}
                <div className="mt-2">
                  <UpgradePlanCard location="sidebar" onDismiss={dismissUpgrade} />
                </div>
              </>
            )}
          </nav>

          {/* User section */}
          <div className="border-t border-sidebar-border p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-hover text-xs font-semibold text-sidebar-foreground">
                {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>
              <div className="min-w-0 flex-1 truncate">
                <p className="truncate text-sm font-medium text-sidebar-foreground">
                  {user?.name}
                </p>
                <p className="truncate text-xs text-sidebar-muted">
                  {user?.email}
                </p>
              </div>
              {/* Appearance switch — inverse tone for the constant-dark rail */}
              <ThemeToggle tone="inverse" className="shrink-0" />
            </div>
            <div className="mt-3">
              <button
                onClick={handleLogout}
                className="flex min-h-11 w-full items-center justify-center rounded-sm border border-sidebar-border px-3 text-sm font-medium text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-accent pointer-fine:min-h-8"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area — min-w-0 so wide children (scrollable tab strips,
          long unbreakable strings) scroll inside their own panel instead of
          stretching the page wider than a phone viewport */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar (mobile) */}
        <header className="sticky top-0 z-30 border-b border-neutral-200 bg-surface/95 backdrop-blur lg:hidden">
          <div className="flex h-12 items-center justify-between px-2">
            <button
              type="button"
              onClick={() => {
                setSidebarOpen(true);
              }}
              className="flex min-h-11 min-w-11 items-center justify-center rounded-md text-neutral-600 transition-colors hover:bg-neutral-100"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <span className="truncate text-base font-semibold text-neutral-900">
              {currentCompany?.companyName ?? "Dashboard"}
            </span>
            <div className="flex items-center gap-0.5">
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 py-4 sm:px-6 lg:px-8 lg:py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
